import type { Language, TranslationFunction, TranslationParams, Translations } from './types';

const STORAGE_KEY = 'zlinkx_language';
type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function isTranslations(value: unknown): value is Translations {
  if (!isRecord(value)) {
    return false;
  }

  return isRecord(value.common) && isRecord(value.auth) && isRecord(value.errors);
}

function createEmptyTranslations(): Translations {
  return {
    common: {},
    auth: {},
    errors: {},
  } as unknown as Translations;
}

export function getStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'zh' || stored === 'fr') {
      return stored;
    }
  } catch {
    // localStorage might not be available
  }
  return 'en';
}

export function storeLanguage(language: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // localStorage might not be available
  }
}

export function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  
  return template.replace(/\{\{(\w+)\}\}/g, (match: string, key: string) => {
    return params[key]?.toString() || match;
  });
}

export function getNestedValue(obj: unknown, path: string): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;
  
  for (const key of keys) {
    if (isRecord(current) && key in current) {
      current = current[key];
    } else {
      return undefined;
    }
  }
  
  return typeof current === 'string' ? current : undefined;
}

export function createTranslationFunction(
  translations: Translations,
  fallbackTranslations?: Translations
): TranslationFunction {
  return (key: string, params?: TranslationParams): string => {
    const value = getNestedValue(translations, key);
    
    if (value) {
      return interpolate(value, params);
    }
    
    // Try fallback language (English)
    if (fallbackTranslations) {
      const fallbackValue = getNestedValue(fallbackTranslations, key);
      if (fallbackValue) {
        return interpolate(fallbackValue, params);
      }
    }
    
    // Return key if translation not found
    console.warn(`Translation not found for key: ${key}`);
    return key;
  };
}

export async function loadTranslations(language: Language): Promise<Translations> {
  try {
    const loadedModule: unknown = await import(`./locales/${language}.json`);
    if (isRecord(loadedModule)) {
      const defaultExport = loadedModule.default;
      if (isTranslations(defaultExport)) {
        return defaultExport;
      }
    }

    throw new Error(`Invalid translation module shape for language: ${language}`);
  } catch (error) {
    console.error(`Failed to load translations for language: ${language}`, error);
    // Return empty translations object as fallback
    return createEmptyTranslations();
  }
}
